import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  AmountInput,
  Button,
  Icon,
  Modal,
  Table,
  Tooltip
} from '@components/index';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT
} from '@constants/generic';
import { customTermFeeConfigTableColumn } from '@features/group-management/constants';
import {
  AmountChangeParams,
  FeeCategoryInputType
} from '@features/group-management/types';
import { FeeConfigurationType, TermsList, ValidationError } from '@types';
import {
  getAmountErrorMessage,
  getMaxAmountErrorMessage
} from '@utils/generic';

import TermAmountInput from './TermAmountInput';

import theme from '../../../../../themes/colors';

interface TermDueAmountFieldProps {
  rowIndex: number;
  row: TermsList;
  isFeeCategoryEnabled: boolean;
  feeCategoryData: FeeConfigurationType[];
  onFeeCategorySave: (params: FeeConfigurationType[], index: number) => void;
  onAmountChange: (dueAmount: string, memberIndex: number) => void;
  isDisabled?: boolean;
}

const TermDueAmountField = ({
  rowIndex,
  row,
  isFeeCategoryEnabled,
  feeCategoryData,
  onAmountChange,
  onFeeCategorySave,
  isDisabled
}: TermDueAmountFieldProps) => {
  const { orgConfig } = useSelector((state: any) => state.user);

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;

  const [isFeePopupOpen, setIsFeePopupOpen] = useState(false);
  const [error, setError] = useState<ValidationError>({});
  const [formattedFeeCategories, setFormattedFeeCategories] = useState<
    FeeCategoryInputType[]
  >([]);

  const handleCancel = () => {
    setIsFeePopupOpen(false);
  };

  const validateAmount = (value: string) => {
    if (
      value.length &&
      Number(value) > 0 &&
      Number(value) < Number(groupMemberMinAmount)
    )
      setError({
        ...error,
        amount: getAmountErrorMessage(groupMemberMinAmount)
      });
    else if (value.length && Number(value) > Number(groupMemberMaxAmount))
      setError({
        ...error,
        amount: getMaxAmountErrorMessage(groupMemberMaxAmount)
      });
    else setError({ ...error, amount: '' });
  };

  const handleSave = () => {
    onFeeCategorySave(
      feeCategoryData.map((feeConfig, index) => ({
        ...feeConfig,
        amount: formattedFeeCategories[index].amount.toString()
      })),
      rowIndex
    );
    const totalAmount = formattedFeeCategories.reduce(
      (sum, feeConfig) => sum + Number(feeConfig.amount),
      0
    );
    validateAmount(totalAmount.toString());
    setIsFeePopupOpen(false);
  };

  const onAmountBlur = (value: string) => {
    if (value.length && Number(value) < 0) {
      setError({
        ...error,
        amount: 'Amount cannot be less than 0'
      });
    }
    if (
      value.length &&
      Number(value) > 0 &&
      Number(value) < Number(groupMemberMinAmount)
    )
      setError({
        ...error,
        amount: getAmountErrorMessage(groupMemberMinAmount)
      });

    if (value.length && Number(value) > Number(groupMemberMaxAmount))
      setError({
        ...error,
        amount: getMaxAmountErrorMessage(groupMemberMaxAmount)
      });
  };

  const onCategoryAmountBlur = (value: string, index: number) => {
    if (value.length && Number(value) < 0) {
      setError({
        ...error,
        [`feeAmount_${index}`]: 'Amount cannot be less than 0'
      });
    }

    if (value.length && Number(value) > Number(groupMemberMaxAmount))
      setError({
        ...error,
        [`feeAmount_${index}`]: getMaxAmountErrorMessage(groupMemberMaxAmount)
      });
  };

  const onAmountClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isFeeCategoryEnabled) {
      setIsFeePopupOpen(true);
    }
  };

  const handleAmountChange = (value: string) => {
    if (!isFeeCategoryEnabled)
      onAmountChange(Number(value)?.toString() || '', rowIndex);
    setError({ ...error, amount: '' });
  };

  const handleCategoryAmountChange = ({ value, index }: AmountChangeParams) => {
    setError({ ...error, [`feeAmount_${index}`]: '' });

    const updatedCategories = [...formattedFeeCategories];
    updatedCategories[index].amount = Number(value)?.toString() || '0';
    setFormattedFeeCategories(updatedCategories);
  };

  // const getFeeConfigValue = (term: TermsList, categoryItem: any) => {
  //   const category = term?.feeCategoryData?.find(
  //     (item) =>
  //       item.id === (categoryItem.branch_fee_category_id ?? categoryItem.id)
  //   );
  //   return category?.value || '0';
  // };

  const isSaveDisabled =
    formattedFeeCategories.some((_, index) => !!error[`feeAmount_${index}`]) ||
    (isFeeCategoryEnabled &&
      formattedFeeCategories.filter((item) => item.amount === '').length > 0);

  const modifiedTableData = formattedFeeCategories?.map(
    (categoryRow, index) => ({
      id: categoryRow.category,
      category: (
        <div className="max-w-125 truncate">{categoryRow.category}</div>
      ),
      amount: (
        <div className="w-[350px] text-base text-content">
          <AmountInput
            value={categoryRow.amount || '0'}
            onAmountChange={(newValue) => {
              handleCategoryAmountChange({
                index,
                value: newValue
              });
            }}
            maxAmount={groupMemberMaxAmount}
            handleInputBlur={(value) => onCategoryAmountBlur(value, index)}
            error={!!error[`feeAmount_${index}`]}
            errorMessage={error[`feeAmount_${index}`]}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
      )
    })
  );

  useEffect(() => {
    if (feeCategoryData && feeCategoryData.length > 0) {
      const formattedFeeCategoriesData = feeCategoryData.map((feeCategory) => ({
        category: feeCategory.category,
        amount: feeCategory.amount || '0'
      }));
      setFormattedFeeCategories(formattedFeeCategoriesData);

      if (isFeeCategoryEnabled)
        onAmountChange(
          formattedFeeCategoriesData
            ?.reduce((sum, item) => sum + Number(item.amount), 0)
            .toString() || '0',
          rowIndex
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(feeCategoryData), isFeeCategoryEnabled]);

  return (
    <>
      <TermAmountInput
        readOnly={isFeeCategoryEnabled || isDisabled}
        className="max-w-60"
        inputClassNames={`${
          isFeeCategoryEnabled ? 'cursor-not-allowed opacity-40' : ''
        }`}
        value={row.dueAmount}
        onClick={(e) => onAmountClick(e)}
        onAmountChange={handleAmountChange}
        disabled={isFeeCategoryEnabled || isDisabled}
        {...(!isFeeCategoryEnabled && {
          handleInputBlur: onAmountBlur
        })}
        error={!!error.amount}
        errorMessage={error.amount}
        role="presentation"
        onKeyDown={(e) => e.stopPropagation()}
        tooltipId={row.id}
        isFeeCategoryEnabled={isFeeCategoryEnabled}
        isFieldDisabled={isDisabled}
        // onClick={(e) => e.stopPropagation()}
      />

      <Tooltip
        id={`${row.id}_term-due-amount`}
        variant="light"
        place="bottom"
        className="shadow-md"
      >
        {isFeeCategoryEnabled && (
          <div className="w-60">
            <p className="mb-3 font-semibold">Fee Categories</p>
            {formattedFeeCategories?.map((feeCategory) => (
              <div
                className="flex justify-between gap-y-2"
                key={feeCategory.category}
              >
                <p className="text-sm text-grey">{feeCategory.category}</p>
                <p className="text-sm text-grey">
                  <span className="mr-0.25 !font-inter">₹</span>
                  {feeCategory.amount}
                </p>
              </div>
            ))}
          </div>
        )}
      </Tooltip>
      <Modal
        isOpen={isFeePopupOpen}
        onCancel={handleCancel}
        isCloseIconRequired
        className="w-[650px] cursor-default bg-white py-6"
      >
        <div className="space-y-6">
          <p className="border-b px-6 pb-4 font-medium">{`${row.term} - Fee Category`}</p>
          {isFeeCategoryEnabled && (
            <div className="border-b px-6">
              <div className="flex h-full max-h-80 flex-col justify-start rounded-lg border border-grey30 p-4">
                <Table
                  columns={customTermFeeConfigTableColumn}
                  data={modifiedTableData}
                />
              </div>

              <div className="flex items-center gap-1 py-4 text-base text-grey">
                <Icon name="alert-circle" stroke={theme.subText} />
                If a fee category doesn&apos;t apply, simply enter
                &quot;₹0&quot;
              </div>
            </div>
          )}
          <div className="flex w-full justify-end px-6">
            {/* <Button
              label="Cancel"
              handleClick={handleCancel}
              variant="secondary"
              size="medium"
              className="w-full"
            /> */}
            <Button
              disabled={isSaveDisabled}
              label="Save"
              handleClick={handleSave}
              variant="primary"
              size="small"
              className="w-50"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TermDueAmountField;
